var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar, Stack, Typography } from '@mui/material';
import { Tabs as AuraTabs } from '@devx/1ux-aura-react-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import OsaRiskCard from '../../components/OsaRiskCard/OsaRiskCard';
import TopBar from '../../components/TopBar/TopBar';
import ManageAlertModal from '../../components/ManageAlertModal/ManageAlertModal';
import { fetchPatientData, clearError as clearOsaError } from '../../store/osaRisk/osaRiskSlice';
import { openManageAlertModal, closeManageAlertModal, showSnackbar, closeSnackbar, setShowAssessmentHistory } from '../../store/ui/uiSlice';
import { fetchPatientTabs } from '../../store/tabs/tabsSlice';
import ManageOsaRisk from '../../components/ManageOsaRisk/ManageOsaRisk';
import OsaEducationDocument from '../../components/OsaEducationDocument';
import ReferralTimeline from '../../components/ReferralTimeline';
import SleepStudyResults from '../../components/SleepStudyResults/SleepStudyResults';
import { manageAlert } from '../../services/manageAlertService';
import { useReportStatus } from '../../hooks/useReportStatus';
import styles from './HomePage.module.scss';
import AssessmentHistory from '../../components/AssessmentHistory/AssessmentHistory';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { formatDobWithAge } from '../../utils/dateTime';
import { initializePendo, trackPendoEvent, getPatientLaunchedAt } from '../../services/pendoService';
import { PATIENT_CLOSE_IDLE_TIMEOUT_MS } from '../../constants/pendo';
import { PATIENT_CLOSED } from '../../constants/pendoEvents';
import LegalDocumentModal from '../../components/LegalDocumentModal';
/**
 * Desired tab display order.
 * Tabs returned by the API are sorted according to this sequence.
 */
const TAB_ORDER = ['clinical_options', 'status', 'patient_education', 'sleep_study_results'];
const HomePage = () => {
    var _a;
    const { t } = useTranslation('translation', {
        keyPrefix: 'HOME_PAGE'
    });
    const dispatch = useDispatch();
    const idleTimerRef = useRef(null);
    const currentPatientIdRef = useRef(null);
    const patientClosedSentRef = useRef(false);
    const previousAuthStateRef = useRef(false);
    // Local state (UI-only, not shared)
    const [manageAlertAnchorEl, setManageAlertAnchorEl] = useState(null);
    const [legalDocumentType, setLegalDocumentType] = useState(null);
    // Get state from Redux
    const { patientId, patientName, patientBirthDate, patientGender, isAuthenticated, fhirUserId, providerCode } = useSelector((state) => state.smart);
    const { aiAvailable } = useSelector((state) => state.smart);
    const { loading } = useSelector((state) => state.osaRisk);
    const { showAssessmentHistory, isManageAlertModalOpen, snackbarMessage, snackbarSeverity } = useSelector((state) => state.ui);
    const { tabs, loading: tabsLoading } = useSelector((state) => state.tabs);
    const hasStatusTab = aiAvailable && tabs.some((tab) => tab.name === 'status');
    // Only check report status when an order exists (status tab is present)
    const { hasNewReport, refetch: refetchReportStatus } = useReportStatus({
        patientId: patientId || undefined,
        providerId: providerCode || 'virtuox',
        enabled: !!patientId && hasStatusTab
    });
    // When tabs are refreshed and include a "status" tab, switch back to tabs view
    useEffect(() => {
        if (hasStatusTab && showAssessmentHistory) {
            dispatch(setShowAssessmentHistory(false));
        }
    }, [hasStatusTab, showAssessmentHistory, dispatch]);
    // Fetch tabs configuration first, then OSA factors.
    // /tabs lazily triggers the first-time ad-hoc ML inference for patients with
    // no prior bulk-export result (~5s: sequential FHIR fetch + ML submit/poll).
    // /osa-factors only reads existing inference data, so firing it in parallel
    // with /tabs would 404 on a patient's very first launch, before the ad-hoc
    // inference has been persisted. Waiting for /tabs to resolve first avoids that.
    useEffect(() => {
        if (!isAuthenticated || !patientId) {
            return;
        }
        const loadTabsThenOsaFactors = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                yield dispatch(fetchPatientTabs({ pageName: 'HomePage', patientId })).unwrap();
            }
            catch (_a) {
                // tabs fetch failed
            }
            // Patient may have switched while /tabs was in flight — skip the now-stale
            // /osa-factors call so it can't overwrite state with the wrong patient's data.
            if (currentPatientIdRef.current !== patientId) {
                return;
            }
            // When the backend has disallowed AI scoring (e.g. patient under 18), we
            // still load demographics and tabs (so Patient education stays visible) but
            // skip the /osa-factors call — there is no inference to retrieve. The card
            // renders the "No OSA risk detected" state via the aiUnavailable prop.
            dispatch(fetchPatientData({ patientId, patientName, skipOsaFactors: !aiAvailable }))
                .unwrap()
                .catch(() => {
                // patient data fetch failed
            });
        });
        void loadTabsThenOsaFactors();
    }, [isAuthenticated, patientId, patientName, aiAvailable, dispatch]);
    useEffect(() => {
        var _a, _b;
        const envName = ((_a = window.__ENV) === null || _a === void 0 ? void 0 : _a.ENV_NAME) ? `_${window.__ENV.ENV_NAME}` : '';
        const baseVisitorId = (fhirUserId === null || fhirUserId === void 0 ? void 0 : fhirUserId.trim()) ? fhirUserId : 'fhir_user';
        const visitorId = `${baseVisitorId}${envName}`;
        initializePendo({
            visitorId,
            accountId: providerCode || ((_b = window.__ENV) === null || _b === void 0 ? void 0 : _b.ENV_NAME) || 'ehr-screener'
        });
    }, [patientId, fhirUserId, providerCode]);
    useEffect(() => {
        currentPatientIdRef.current = patientId;
        patientClosedSentRef.current = false;
    }, [patientId]);
    useEffect(() => {
        previousAuthStateRef.current = isAuthenticated;
    }, [isAuthenticated]);
    useEffect(() => {
        const clearIdleTimer = () => {
            if (idleTimerRef.current != null) {
                window.clearTimeout(idleTimerRef.current);
                idleTimerRef.current = null;
            }
        };
        const sendPatientClosedEvent = (reason) => {
            const activePatientId = currentPatientIdRef.current;
            if (!activePatientId || patientClosedSentRef.current)
                return;
            const launchedAt = getPatientLaunchedAt();
            const sessionDurationMs = launchedAt != null ? Date.now() - launchedAt : undefined;
            trackPendoEvent(PATIENT_CLOSED, Object.assign({ description: `Patient closed due to ${reason}`, patient_id: activePatientId, close_reason: reason }, (sessionDurationMs != null ? { session_duration_ms: sessionDurationMs } : {})));
            patientClosedSentRef.current = true;
            clearIdleTimer();
        };
        const scheduleIdleTimer = () => {
            if (!isAuthenticated || !currentPatientIdRef.current)
                return;
            clearIdleTimer();
            idleTimerRef.current = window.setTimeout(() => {
                sendPatientClosedEvent('idle_timeout');
            }, PATIENT_CLOSE_IDLE_TIMEOUT_MS);
        };
        const handleActivity = () => {
            if (!isAuthenticated || !currentPatientIdRef.current || patientClosedSentRef.current)
                return;
            scheduleIdleTimer();
        };
        const handlePageHide = () => {
            sendPatientClosedEvent('browser_closed');
        };
        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, handleActivity, { passive: true });
        });
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('beforeunload', handlePageHide);
        scheduleIdleTimer();
        return () => {
            clearIdleTimer();
            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, handleActivity);
            });
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('beforeunload', handlePageHide);
        };
    }, [isAuthenticated, patientId]);
    useEffect(() => {
        if (previousAuthStateRef.current && !isAuthenticated) {
            const activePatientId = currentPatientIdRef.current;
            if (activePatientId && !patientClosedSentRef.current) {
                const launchedAt = getPatientLaunchedAt();
                const sessionDurationMs = launchedAt != null ? Date.now() - launchedAt : undefined;
                trackPendoEvent(PATIENT_CLOSED, Object.assign({ patient_id: activePatientId, close_reason: 'session_expired' }, (sessionDurationMs != null ? { session_duration_ms: sessionDurationMs } : {})));
                patientClosedSentRef.current = true;
            }
            if (idleTimerRef.current != null) {
                window.clearTimeout(idleTimerRef.current);
                idleTimerRef.current = null;
            }
        }
        previousAuthStateRef.current = isAuthenticated;
    }, [isAuthenticated]);
    /**
     * Build tab items from the API response, with a fallback to default tabs.
     */
    const tabItems = useMemo(() => {
        /**
         * Map a tab component name to the corresponding React component.
         */
        const getTabContent = (tab) => {
            switch (tab.name) {
                case 'clinical_options':
                    return _jsx(ManageOsaRisk, {});
                case 'patient_education':
                    return _jsx(OsaEducationDocument, {});
                case 'status':
                    return _jsx(ReferralTimeline, {});
                case 'sleep_study_results':
                    return _jsx(SleepStudyResults, { onReportStatusChange: refetchReportStatus });
                default:
                    return null;
            }
        };
        // When AI is not available for this patient (e.g. under 18), only Patient
        // education is relevant — clinical options / status / sleep study results
        // have no meaning without a prediction. Filter defensively in case the
        // backend page-config still returns the full tab set.
        const visibleTabs = aiAvailable ? tabs : tabs.filter((tab) => tab.name === 'patient_education');
        if (visibleTabs.length > 0) {
            const sorted = [...visibleTabs].sort((a, b) => {
                const indexA = TAB_ORDER.indexOf(a.name);
                const indexB = TAB_ORDER.indexOf(b.name);
                // Tabs not in the order list go to the end
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });
            return sorted.map((tab) => ({
                label: tab.displayName,
                value: tab.name,
                content: getTabContent(tab)
            }));
        }
        // Fallback: show default tabs when API hasn't responded yet
        return [
            {
                label: t('TAB_PATIENT_EDUCATION'),
                value: 'patient_education',
                content: _jsx(OsaEducationDocument, {})
            }
        ];
    }, [tabs, t, refetchReportStatus, aiAvailable]);
    const handleCloseSnackbar = () => {
        dispatch(closeSnackbar());
    };
    /**
     * Handle tab change
     * Note: Individual reports are now marked as viewed when clicked, not at tab level
     */
    const handleTabChange = (_event, _newTabValue) => __awaiter(void 0, void 0, void 0, function* () {
        // No action needed - reports are marked viewed individually when clicked
        // The hasNewReport badge is updated by backend based on individual report views
    });
    const handleManageAlertClick = (event) => {
        setManageAlertAnchorEl(event.currentTarget);
        dispatch(openManageAlertModal());
    };
    const handleManageAlertClose = () => {
        setManageAlertAnchorEl(null);
        dispatch(closeManageAlertModal());
    };
    const handleManageAlertSave = (reason, action, otherReason) => __awaiter(void 0, void 0, void 0, function* () {
        // Only call backend when dismissing the alert.
        if (action !== 'dismiss')
            return;
        if (!patientId) {
            const err = new Error('Patient ID is missing. Unable to manage alert.');
            dispatch(showSnackbar({ message: err.message, severity: 'error' }));
            throw err;
        }
        try {
            dispatch(clearOsaError());
            yield manageAlert(Object.assign({ patientId,
                reason, dismiss: true }, (reason === 'OTHER' && otherReason ? { otherReason } : {})));
        }
        catch (err) {
            const e = err;
            const errorMessage = e.message || 'Failed to dismiss alert. Please try again.';
            dispatch(showSnackbar({ message: errorMessage, severity: 'error' }));
            throw e;
        }
    });
    const openLegalDocument = (documentType) => () => {
        setLegalDocumentType(documentType);
    };
    const closeLegalDocument = () => {
        setLegalDocumentType(null);
    };
    // Loading state while fetching patient data. Includes tabsLoading because
    // /osa-factors is now dispatched only after /tabs resolves (see effect above),
    // so osaRisk.loading alone would miss that wait on a patient's first launch.
    if (loading || tabsLoading) {
        return (_jsxs(Box, { className: styles.page_container, children: [_jsx(TopBar, { onManageAlert: handleManageAlertClick }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }, children: _jsxs(Stack, { spacing: 2, alignItems: "center", children: [_jsx(CircularProgress, { size: 60 }), _jsx(Typography, { variant: "h6", color: "text.secondary", children: t('LOADING_PATIENT_DATA') })] }) })] }));
    }
    return (_jsxs(Box, { className: styles.page_container, children: [_jsx(TopBar, { onManageAlert: handleManageAlertClick }), _jsxs(Box, { className: styles.sticky_heading, children: [_jsx(Typography, { variant: "h6", sx: { fontSize: '2.0rem' }, children: t('HEADING_PREFIX') }), _jsxs(Box, { className: styles.patient_details, children: [_jsxs(Box, { className: styles.patient_detail_item, children: [_jsxs(Typography, { variant: "h4", className: styles.patient_detail_label, children: [t('PATIENT_NAME_LABEL'), ":"] }), _jsx(Typography, { variant: "h4", className: styles.patient_detail_value, children: patientName })] }), _jsxs(Box, { className: styles.patient_detail_item, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '4px' }, children: [_jsx(CalendarTodayOutlinedIcon, { className: styles.calendar_logo, sx: { fontSize: '18px' } }), _jsxs(Typography, { variant: "h4", className: styles.patient_detail_label, children: [t('DATE_OF_BIRTH_LABEL'), ":"] })] }), _jsx(Typography, { variant: "h4", className: styles.patient_detail_value, children: formatDobWithAge(patientBirthDate) })] }), _jsxs(Box, { className: styles.patient_detail_item, children: [_jsxs(Typography, { variant: "h4", className: styles.patient_detail_label, children: [t('GENDER_LABEL'), ":"] }), _jsx(Typography, { variant: "h4", className: styles.patient_detail_value, children: patientGender })] })] })] }), _jsx(Box, { className: styles.content_wrapper, children: _jsxs(Box, { className: styles.layout_container, children: [_jsx(OsaRiskCard, { aiUnavailable: !aiAvailable }), _jsxs(Box, { className: styles.main_content, children: [showAssessmentHistory ? (_jsx(AssessmentHistory, {})) : (_jsx(Box, { className: hasNewReport ? styles.tabs_with_badge : '', children: !tabsLoading && (_jsx(AuraTabs, { id: "osa-risk-tabs", ariaLabel: t('TABS_ARIA_LABEL'), defaultValue: ((_a = tabItems[0]) === null || _a === void 0 ? void 0 : _a.value) || 'clinical_options', tabItems: tabItems, onChange: handleTabChange })) })), _jsx(ManageAlertModal, { open: isManageAlertModalOpen, onClose: handleManageAlertClose, onSave: handleManageAlertSave, anchorEl: manageAlertAnchorEl })] })] }) }), _jsx(Box, { component: "footer", className: styles.legal_footer, children: _jsxs(Box, { className: styles.legal_footer_content, children: [_jsxs(Box, { className: styles.legal_footer_links, children: [_jsx("button", { className: styles.legal_footer_button, type: "button", onClick: openLegalDocument('terms'), children: t('TERMS_OF_USE') }), _jsx("button", { className: styles.legal_footer_button, type: "button", onClick: openLegalDocument('privacy'), children: t('PRIVACY_POLICY') })] }), _jsx(Typography, { className: styles.legal_footer_copyright, children: t('COPYRIGHT') })] }) }), _jsx(LegalDocumentModal, { documentType: legalDocumentType, onClose: closeLegalDocument }), _jsx(Snackbar, { open: !!snackbarMessage, autoHideDuration: 6000, onClose: handleCloseSnackbar, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: snackbarSeverity, children: snackbarMessage }) })] }));
};
export default HomePage;
